/**
 * FormLibrary — grid of form schema cards.
 *
 * Features:
 *   - AI mode badge (AI Only / Toggle / Manual)
 *   - Public/Private badge + copy public URL
 *   - Preview → opens FormPreviewSlideOver
 *   - Edit → opens FormBuilderPage
 *   - Delete with confirmation
 *   - "New Form" button
 */

import React, { useEffect, useState, useCallback } from 'react';
import { forms as formsApi, FormSchema } from '../../../lib/api.js';
import FormPreviewSlideOver from '../../../components/forms/FormPreviewSlideOver.js';

interface Props {
  onEdit:          (schema: FormSchema) => void;
  onNewForm:       () => void;
  refreshSignal?:  number;
}

interface PreviewTarget {
  id:   string;
  name: string;
}

const AI_MODE_LABELS: Record<string, string> = {
  ai_only:   '🤖 AI Only',
  toggle:    '⇄ AI + Manual',
  form_only: '📋 Manual',
};

export default function FormLibrary({ onEdit, onNewForm, refreshSignal }: Props): React.ReactElement {
  const [schemas,  setSchemas]  = useState<FormSchema[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [preview,  setPreview]  = useState<PreviewTarget | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await formsApi.schemas.list();
      if (res.success && res.data) setSchemas(res.data);
      else setError(res.error ?? 'Failed to load forms');
    } catch {
      setError('Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load, refreshSignal]);

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      await formsApi.schemas.delete(id);
      setSchemas((prev) => prev.filter((s) => s.id !== id));
    } catch {
      alert('Failed to delete form');
    } finally {
      setDeleting(null);
    }
  };

  const copyPublicUrl = (slug: string, locationId: string) => {
    const url = `${window.location.origin}/f/${locationId}/${slug}`;
    navigator.clipboard.writeText(url).then(() => alert('Public URL copied!'));
  };

  if (loading) return <div style={styles.center}><p style={styles.muted}>Loading forms…</p></div>;
  if (error)   return <div style={styles.center}><p style={{ color: '#c0392b' }}>{error}</p></div>;

  return (
    <div style={styles.container}>
      <div style={styles.toolbar}>
        <h3 style={styles.heading}>Form Library</h3>
        <button style={styles.newBtn} onClick={onNewForm}>+ New Form</button>
      </div>

      {schemas.length === 0 ? (
        <div style={styles.empty}>
          <span style={{ fontSize: 40 }}>📋</span>
          <p style={styles.muted}>No forms yet. Create one to get started.</p>
          <button style={styles.newBtn} onClick={onNewForm}>+ New Form</button>
        </div>
      ) : (
        <div style={styles.grid}>
          {schemas.map((s) => (
            <div key={s.id} style={styles.card}>
              {/* Header */}
              <div style={styles.cardHeader}>
                <span style={styles.cardName}>{s.name}</span>
                <span style={{
                  ...styles.aiModeBadge,
                  background: s.aiMode === 'ai_only' ? '#eff6ff' :
                              s.aiMode === 'toggle'  ? '#f0fdf4' : '#f9fafb',
                  color:      s.aiMode === 'ai_only' ? '#2563eb' :
                              s.aiMode === 'toggle'  ? '#15803d' : '#374151',
                }}>
                  {AI_MODE_LABELS[s.aiMode] ?? s.aiMode}
                </span>
              </div>

              {/* Meta */}
              {s.description && <p style={styles.desc}>{s.description}</p>}
              <div style={styles.cardMeta}>
                <span style={styles.fieldCount}>{s.fieldCount} fields</span>
                {s.isPublic ? (
                  <span style={styles.publicBadge}>🌐 Public</span>
                ) : (
                  <span style={styles.privateBadge}>🔒 Private</span>
                )}
              </div>

              {/* Public URL */}
              {s.isPublic && s.publicSlug && (
                <div style={styles.publicUrlRow}>
                  <button
                    style={styles.copyUrlBtn}
                    onClick={() => copyPublicUrl(s.publicSlug!, s.locationId)}
                    title="Copy public form URL"
                  >
                    📋 Copy URL
                  </button>
                  <a
                    style={styles.openUrlBtn}
                    href={`/f/${s.locationId}/${s.publicSlug}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Open form in new tab"
                  >
                    ↗ Open
                  </a>
                </div>
              )}

              {/* Actions */}
              <div style={styles.cardActions}>
                <button
                  style={styles.previewBtn}
                  onClick={() => setPreview({ id: s.id, name: s.name })}
                  title="Preview this form"
                >
                  👁 Preview
                </button>
                <button style={styles.editBtn} onClick={() => onEdit(s)}>Edit</button>
                <button
                  style={styles.deleteBtn}
                  onClick={() => handleDelete(s.id, s.name)}
                  disabled={deleting === s.id}
                >
                  {deleting === s.id ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Preview Slide-over */}
      {preview && (
        <FormPreviewSlideOver
          formId={preview.id}
          formName={preview.name}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '16px 20px', height: '100%', overflowY: 'auto' },
  toolbar:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  heading:   { margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' },
  newBtn:    { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  center:    { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 },
  muted:     { color: '#9ca3af', fontSize: 14, margin: 0 },
  empty:     { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 },
  grid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 },
  card:      { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
  cardHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 },
  cardName:  { fontWeight: 600, fontSize: 14, color: '#111827', flex: 1 },
  aiModeBadge: { fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0 },
  desc:      { margin: 0, fontSize: 12, color: '#6b7280', lineHeight: 1.5 },
  cardMeta:  { display: 'flex', alignItems: 'center', gap: 8 },
  fieldCount: { fontSize: 12, color: '#9ca3af' },
  publicBadge:  { fontSize: 11, background: '#ecfdf5', color: '#059669', padding: '1px 6px', borderRadius: 4 },
  privateBadge: { fontSize: 11, background: '#f3f4f6', color: '#6b7280', padding: '1px 6px', borderRadius: 4 },
  publicUrlRow: { display: 'flex', gap: 6 },
  copyUrlBtn: { background: 'none', border: '1px dashed #d1d5db', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11, color: '#6b7280' },
  openUrlBtn: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, padding: '3px 8px', fontSize: 11, color: '#059669', fontWeight: 500, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' },
  cardActions: { display: 'flex', gap: 6, marginTop: 4 },
  previewBtn:  { padding: '5px 10px', border: '1px solid #bae6fd', borderRadius: 5, background: '#f0f9ff', cursor: 'pointer', fontSize: 12, color: '#0369a1', fontWeight: 500 },
  editBtn:     { flex: 1, padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 5, background: '#f9fafb', cursor: 'pointer', fontSize: 12, color: '#374151' },
  deleteBtn:   { padding: '5px 10px', border: '1px solid #fca5a5', borderRadius: 5, background: '#fef2f2', cursor: 'pointer', fontSize: 12, color: '#b91c1c' },
};
